/**
 * InlineTextEditor - Inline text editing for Page Builder elements
 * 
 * Features:
 * - ContentEditable-based editing
 * - Enter saves, Escape cancels
 * - Maintains typography stability
 * - Character count limit enforcement
 */
import React, { useRef, useEffect, useCallback } from 'react';

const InlineTextEditor = ({
  value,
  onChange,
  onSave,
  onCancel,
  isEditing,
  maxLength,
  className = '',
  style = {},
  placeholder = 'Enter text...',
}) => {
  const editorRef = useRef(null);
  const wasEditingRef = useRef(false);

  // Focus and select all when entering edit mode
  useEffect(() => {
    if (isEditing && !wasEditingRef.current && editorRef.current) {
      // Set the initial text content
      editorRef.current.innerText = value || '';
      editorRef.current.focus();
      
      // Select all text
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
    wasEditingRef.current = isEditing;
  }, [isEditing, value]);

  const handleInput = useCallback((e) => {
    let newValue = e.target.innerText;
    
    // Enforce max length
    if (maxLength && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
      e.target.innerText = newValue;
      
      // Move cursor to end
      const range = document.createRange();
      range.selectNodeContents(e.target);
      range.collapse(false);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    onChange?.(newValue);
  }, [maxLength, onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const currentText = editorRef.current?.innerText || '';
      onSave?.(currentText);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (editorRef.current) {
        editorRef.current.innerText = value || '';
      }
      onCancel?.();
    }
  }, [value, onSave, onCancel]);

  const handleBlur = useCallback(() => {
    if (isEditing) {
      const currentText = editorRef.current?.innerText || '';
      onSave?.(currentText);
    }
  }, [isEditing, onSave]);

  if (!isEditing) {
    return (
      <span className={className} style={style}>
        {value || placeholder}
      </span>
    );
  }

  return (
    <span
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      className={`outline-none ${className}`}
      style={{
        ...style,
        caretColor: '#f97316', // Orange caret
        minWidth: '20px',
        display: 'inline',
      }}
      data-testid="inline-text-editor"
    />
  );
};

export default InlineTextEditor;
